import React, { useState, useReducer } from 'react';
import Stage from './Stage';
import ResultPage from './ResultPage';
import { stages } from '../data/questions';
import { evaluateStage } from '../utils/scoring';

const initialState = {
  currentStage: 0,
  testStarted: false,
  testCompleted: false,
  allAnswers: {},
  allQuestions: [],
  finalLevel: null,
  stageResults: []
};

function testReducer(state, action) {
  switch (action.type) {
    case 'START_TEST':
      return {
        ...state,
        testStarted: true,
        currentStage: 1
      };
    case 'COMPLETE_STAGE':
      const { evaluation, answers, questions } = action.payload;
      const newState = {
        ...state,
        allAnswers: { ...state.allAnswers, ...answers },
        allQuestions: [...state.allQuestions, ...questions],
        stageResults: [...state.stageResults, evaluation]
      };
      
      if (evaluation.finalLevel) {
        return {
          ...newState,
          testCompleted: true,
          finalLevel: evaluation.finalLevel
        };
      } else if (evaluation.shouldContinue) {
        return {
          ...newState,
          currentStage: evaluation.nextStage
        };
      }
      
      return newState;
    case 'RESTART':
      return initialState;
    default:
      return state;
  }
}

export default function TestEngine() {
  const [state, dispatch] = useReducer(testReducer, initialState);
  const [showInstructions, setShowInstructions] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  
  const handleStart = () => {
    setShowInstructions(true);
  };
  
  const handleBeginTest = () => {
    dispatch({ type: 'START_TEST' });
  };
  
  const handleStageComplete = (score, totalQuestions, answers) => {
    setIsProcessing(true);
    
    setTimeout(() => {
      const currentStageData = stages[state.currentStage - 1];
      const evaluation = evaluateStage(score, totalQuestions, state.currentStage);
      
      dispatch({
        type: 'COMPLETE_STAGE',
        payload: {
          evaluation,
          answers,
          questions: currentStageData.questions
        }
      });
      
      setIsProcessing(false);
    }, 800);
  };
  
  const handleRestart = () => {
    dispatch({ type: 'RESTART' });
    setShowInstructions(false);
  };
  
  if (state.testCompleted) {
    return (
      <ResultPage
        finalLevel={state.finalLevel}
        allAnswers={state.allAnswers}
        allQuestions={state.allQuestions}
        onRestart={handleRestart}
      />
    );
  }
  
  if (isProcessing) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-xl text-gray-700 font-semibold">Processing your results...</p>
        </div>
      </div>
    );
  }
  
  if (state.testStarted) {
    const currentStageData = stages[state.currentStage - 1];
    return (
      <Stage
        key={`stage-${state.currentStage}`}
        stage={currentStageData}
        questions={currentStageData.questions}
        onComplete={handleStageComplete}
        totalStages={6}
      />
    );
  }
  
  if (showInstructions) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4 py-12">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-3xl w-full">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Test Instructions</h2>
          
          <div className="space-y-4 mb-8 text-gray-700">
            <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
              <h3 className="font-semibold text-blue-900 mb-2">📋 Test Structure</h3>
              <p>This adaptive test contains 6 stages (A1 through C2). Each stage has 10-15 questions testing grammar, vocabulary, reading, and writing skills.</p>
            </div>
            
            <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-500">
              <h3 className="font-semibold text-green-900 mb-2">✅ Adaptive System</h3>
              <ul className="list-disc list-inside space-y-1">
                <li>Score 80%+ → Advance to next level</li>
                <li>Score 60-79% → This is your level</li>
                <li>Score below 60% → Previous level is your level</li>
              </ul>
            </div>
            
            <div className="bg-amber-50 p-4 rounded-lg border-l-4 border-amber-500">
              <h3 className="font-semibold text-amber-900 mb-2">⏱️ Time Limit</h3>
              <p>Each stage has a 30-minute time limit. The test will auto-submit when time expires.</p>
            </div>
            
            <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-500">
              <h3 className="font-semibold text-purple-900 mb-2">🔒 Important Rules</h3>
              <ul className="list-disc list-inside space-y-1">
                <li>You cannot go back to previous stages</li>
                <li>Answer all questions before submitting</li>
                <li>Questions are randomized</li>
                <li>Do your best - the test adapts to your level!</li>
              </ul>
            </div>
          </div>
          
          <div className="flex justify-center space-x-4">
            <button
              onClick={() => setShowInstructions(false)}
              className="px-6 py-3 bg-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-400 transition-colors"
            >
              Go Back
            </button>
            <button
              onClick={handleBeginTest}
              className="px-8 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all shadow-lg"
            >
              Begin Test
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center px-4">
      <div className="text-center max-w-2xl">
        <div className="mb-8 animate-fade-in">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">
            English Placement Test
          </h1>
          <p className="text-xl text-gray-600 mb-2">
            Discover Your CEFR Level (A1 - C2)
          </p>
          <p className="text-gray-500">
            Adaptive assessment with IELTS and TOEFL equivalents
          </p>
        </div>
        
        <div className="bg-white rounded-2xl shadow-2xl p-8 mb-8">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            {['A1', 'A2', 'B1', 'B2', 'C1', 'C2'].map((level, index) => (
              <div
                key={level}
                className="bg-gradient-to-br from-blue-500 to-indigo-500 text-white p-4 rounded-lg font-bold text-lg shadow-md"
              >
                {level}
              </div>
            ))}
          </div>
          
          <div className="space-y-3 text-left text-gray-700 mb-6">
            <div className="flex items-start">
              <span className="text-2xl mr-3">📊</span>
              <div>
                <p className="font-semibold">Adaptive Testing</p>
                <p className="text-sm text-gray-600">Test adapts to your performance</p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-2xl mr-3">⏱️</span>
              <div>
                <p className="font-semibold">30 Minutes Per Stage</p>
                <p className="text-sm text-gray-600">Take your time but stay focused</p>
              </div>
            </div>
            <div className="flex items-start">
              <span className="text-2xl mr-3">🎯</span>
              <div>
                <p className="font-semibold">Comprehensive Results</p>
                <p className="text-sm text-gray-600">Get CEFR, IELTS & TOEFL equivalents</p>
              </div>
            </div>
          </div>
        </div>
        
        <button
          onClick={handleStart}
          className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-12 py-4 rounded-xl text-xl font-bold hover:from-blue-700 hover:to-indigo-700 transition-all transform hover:scale-105 shadow-2xl"
        >
          Start Test
        </button>
      </div>
    </div>
  );
}
